import { useEffect, useRef } from "react";
import { GameProvider, useGame } from "./game.js";
import { isOnboarded, rememberOnboarded, saveOnboarded } from "./onboarding.js";
import { go, useRoute, useTelegramBack } from "./router.js";
import { SCREENS } from "./screens/index.js";
import { openedChallenge, WAITING_ALERT } from "./challenge.js";
import { Maintenance } from "./screens/Maintenance.js";
import { BusyQueue, queueDone, Waitlist } from "./screens/Gates.js";
import { ConnectionLost, ScreenBoundary, ServerError } from "./screens/Edge.js";

const INTRO_START = new Set(["App01", "App02", "App03"]);
const BATTLE_SCREENS = new Set(["App12", "App14", "App15", "App16"]);
const SEARCH_SCREENS = new Set(["App11", "App50"]);
/** Money screens stay open during maintenance: a withdrawal is never blocked. */
const WALLET_SCREENS = new Set(["App28", "App28a", "App04", "App39", "App40", "App55", "App71", "App75"]);

// First launch starts at the splash; after onboarding the app opens in the lobby.
function Screens() {
  const route = useRoute(isOnboarded() ? "App09" : "App01");
  useTelegramBack(route);
  const { battle, me, queued, challenge, maintenance, queueScreen, waitlist, connection } = useGame();

  // The intro is once per account, not per phone: a returning player whose phone forgot (new
  // phone, Telegram cleared its storage) skips it as soon as the server says they did it.
  useEffect(() => {
    if (!me) return;
    if (me.onboarded) {
      rememberOnboarded();
      if (INTRO_START.has(route)) go("App09", true);
    } else if (isOnboarded()) saveOnboarded();
  }, [me?.onboarded]);

  // Reopening the app mid-battle goes straight back into it. Only on opening: a battle found by
  // searching in this session already moves screens on its own.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !battle) return;
    restored.current = true;
    if (route === "App11" || route === "App12") return;
    if (battle.state === "live") go("App14", true);
    else if (battle.state === "settling") go("App16", true);
  }, [battle]);

  // Opened from a challenge link: once the player is in (after the intro, for a new one), straight to the challenge.
  useEffect(() => {
    if (route !== "App09" || !(me?.onboarded || isOnboarded())) return;
    // A waiting alert's button: straight to the search it answers.
    const ranks = sessionStorage.getItem("dd:openRanks");
    if (ranks) sessionStorage.removeItem("dd:openRanks");
    const invite = sessionStorage.getItem("dd:openInvite");
    if (invite) sessionStorage.removeItem("dd:openInvite");
    const partners = sessionStorage.getItem("dd:openPartners");
    if (partners) sessionStorage.removeItem("dd:openPartners");
    const events = sessionStorage.getItem("dd:openEvents");
    if (events) sessionStorage.removeItem("dd:openEvents");
    const to = openedChallenge() ? "App22" : sessionStorage.getItem(WAITING_ALERT) ? "App10w" : ranks ? "App23" : invite ? "App27" : partners ? "App60" : events ? "App42" : null;
    if (!to) return;
    const t = setTimeout(() => go(to, true), 0);
    return () => clearTimeout(t);
  }, [route, me?.onboarded]);

  // A challenge waiting for a taker keeps its waiting screen: reopening the app goes back to it.
  const challengeOpen = challenge?.kind === "link" && challenge.state === "open";
  useEffect(() => {
    if (!challengeOpen || route === "App21b" || BATTLE_SCREENS.has(route)) return;
    const t = setTimeout(() => go("App21b", true), 0);
    return () => clearTimeout(t);
  }, [route, challengeOpen]);

  // While a search runs, its screens are the only place to be: no picking a side or a stake again
  // until it ends in a match, a cancel or the time limit. Waits a tick, like the battle rule below.
  useEffect(() => {
    if (!queued || SEARCH_SCREENS.has(route)) return;
    const t = setTimeout(() => go("App11", true), 0);
    return () => clearTimeout(t);
  }, [route, queued]);

  // The battle screens only make sense during a battle. Landing on one afterwards (back, a stale
  // link) goes to the lobby instead of showing a finished battle frozen at 00:00.
  // Waits a tick: when a battle settles, its result screen replaces this one straight away.
  const inBattle = battle?.state === "live" || battle?.state === "settling" || battle?.state === "cancelling";
  useEffect(() => {
    if (!BATTLE_SCREENS.has(route) || inBattle) return;
    const t = setTimeout(() => go("App09", true), 0);
    return () => clearTimeout(t);
  }, [route, inBattle]);

  // The server keeps erroring and nothing has loaded yet: say so, with a reference for support.
  if (connection.failed && !me) return <ServerError />;
  // Sign-ups paused and this is someone new: the waitlist is all there is until they're let in.
  if (waitlist) return <Waitlist place={waitlist} />;
  // Busy: a short wait before the lobby, once per app open. Battles, searches and the wallet are never held up.
  if (queueScreen && !queueDone() && !inBattle && !queued && !BATTLE_SCREENS.has(route) && !WALLET_SCREENS.has(route) && !INTRO_START.has(route)) return <BusyQueue />;
  // Maintenance: no new play, but a battle already running finishes on its own screens, and the wallet stays open.
  if (maintenance && !inBattle && !queued && !BATTLE_SCREENS.has(route) && !WALLET_SCREENS.has(route)) return <Maintenance text={maintenance} />;

  const Screen = SCREENS[route];
  if (!Screen) {
    go("App09", true);
    return null;
  }
  return (
    <>
      <ScreenBoundary route={route}><Screen /></ScreenBoundary>
      {/* Over any screen: the connection dropped. It clears itself when the connection is back. */}
      {connection.lost && <ConnectionLost />}
    </>
  );
}

export function App() {
  return (
    <GameProvider>
      <Screens />
    </GameProvider>
  );
}
